import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import './style.css'
import Button from '../../Buttons'
import CloseIcon from '../../../../assets/icons/actions/close.svg'
import { useUserContext } from '../../../../context/UserContext'
import { UPDATE_EVENT } from '../../../../apollo/mutations/event'
import { gql, useMutation } from '@apollo/client'
import { uploadData, getUrl } from 'aws-amplify/storage'
import dayjs from 'dayjs'
import CustomDatePicker from '../../Input/CustomDatePicker'
import { usePlaceAutocomplete } from '../../../../hooks/usePlaceAutocomplete'
import { fetchTimezoneIdFromLatLng } from '../../../../utils/fetchTimezoneIdFromLatLng'
import moment from 'moment'
import { EVENT_TYPE_OTHER, EVENT_TYPE_OPTIONS, resolveEventType } from '../../../../constants/eventTypes'

const isoToPickerDate = (iso) => {
  if (!iso) return ''
  const [y, m, d] = iso.split('-')
  if (!y || !m || !d) return ''
  return `${m}-${d}-${y}`
}

const pickerToIsoDate = (mdy) => {
  if (!mdy) return ''
  const [m, d, y] = mdy.split('-')
  if (!y || !m || !d) return ''
  return `${y}-${m}-${d}`
}

function normalizeIsoDate(val) {
  if (!val) return ''
  if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val
  const d = moment(val, ['MM-DD-YYYY', 'YYYY-MM-DD'], true)
  return d.isValid() ? d.format('YYYY-MM-DD') : ''
}

const enumerateDates = (startIso, endIso) => {
  if (!startIso || !endIso || endIso < startIso) return []
  const out = []
  const start = new Date(startIso + 'T00:00:00.000Z')
  const end = new Date(endIso + 'T00:00:00.000Z')
  for (let t = start.getTime(); t <= end.getTime(); t += 24 * 60 * 60 * 1000) {
    out.push(new Date(t).toISOString().slice(0, 10))
  }
  return out
}

const formatDateLabel = (iso) => {
  if (!iso) return ''
  const d = new Date(iso + 'T00:00:00.000Z')
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  })
}


function EditEventModal() {
  const {
    setEditModal,
    cardId,
    items,
    setDefaultImage,
    openMessageModal,
    userData,
  } = useUserContext()

  const modalRef = useRef(null)
  const imageRef = useRef(null)
  const addressInputRef = useRef(null)

  const [updateEvent, { loading }] = useMutation(gql(UPDATE_EVENT), {
    refetchQueries: ['listEvents'],
  })

  const [form, setForm] = useState({
    eventName: '',
    description: '',
    city: '',
    state: '',
    country: 'USA',
    eventLocation: '',
    startDate: '',
    endDate: '',
    durationMinutes: 15,
    bufferMinutes: 10,
    capacity: 1,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Chicago',
    visibility: 'ENABLED',
    dailyStartTime: '09:00',
    dailyEndTime: '17:00',
    breakStartTime: '',
    breakEndTime: '',
    fullPaymentAtBooking: false,
  })
  const [dailySchedule, setDailySchedule] = useState([])
  const [eventTypeSelect, setEventTypeSelect] = useState('')
  const [eventTypeOther, setEventTypeOther] = useState('')
  const [hasSponsorship, setHasSponsorship] = useState(false)
  const [sponsorshipTitle, setSponsorshipTitle] = useState('')
  const [placeCoordinates, setPlaceCoordinates] = useState(null)
  const [imageUrl, setImageUrl] = useState('')
  const [uploading, setUploading] = useState(false)
  const [timezoneLookupLoading, setTimezoneLookupLoading] = useState(false)
  const [timezoneFromPinError, setTimezoneFromPinError] = useState(null)
  const [showTimezoneOverride, setShowTimezoneOverride] = useState(false)
  const [formReady, setFormReady] = useState(false)

  const handlePlaceSelected = useCallback((data) => {
    if (!data) {
      setPlaceCoordinates(null)
      return
    }
    setPlaceCoordinates({ latitude: data.latitude, longitude: data.longitude })
    setForm((f) => ({
      ...f,
      city: data.city || f.city,
      state: data.state || f.state,
      country: data.country || f.country,
      eventLocation: data.eventLocation || f.eventLocation,
    }))
    const line = data.formattedAddress || data.eventLocation || ''
    queueMicrotask(() => {
      if (addressInputRef.current) addressInputRef.current.value = line
    })
    setShowTimezoneOverride(false)
    setTimezoneFromPinError(null)
    setTimezoneLookupLoading(true)
    fetchTimezoneIdFromLatLng(data.latitude, data.longitude)
      .then((tz) => {
        setForm((f) => ({ ...f, timezone: tz }))
        setTimezoneFromPinError(null)
      })
      .catch((err) => {
        setTimezoneFromPinError(err?.message || 'Could not load timezone for this location.')
      })
      .finally(() => {
        setTimezoneLookupLoading(false)
      })
  }, [])

  const { mapsLoading, mapsError } = usePlaceAutocomplete(addressInputRef, handlePlaceSelected)

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }))

  useLayoutEffect(() => {
    if (!items?.id) return

    const isoStart = normalizeIsoDate(items.startDate)
    const isoEnd = normalizeIsoDate(items.endDate)

    setForm({
      eventName: items.eventName || '',
      description: items.description || '',
      city: items.city || '',
      state: items.state || '',
      country: items.country || 'USA',
      eventLocation: items.eventLocation || '',
      startDate: isoStart,
      endDate: isoEnd,
      durationMinutes: items.durationMinutes ?? 15,
      bufferMinutes: items.bufferMinutes ?? 0,
      capacity: items.capacity ?? 1,
      timezone:
        items.timezone ||
        Intl.DateTimeFormat().resolvedOptions().timeZone ||
        'America/Chicago',
      visibility: items.visibility || 'ENABLED',
      dailyStartTime: items.dailyStartTime || '09:00',
      dailyEndTime: items.dailyEndTime || '17:00',
      breakStartTime: items.breakStartTime || '',
      breakEndTime: items.breakEndTime || '',
      fullPaymentAtBooking: Boolean(items.fullPaymentAtBooking),
    })

    setDailySchedule(
      (items.dailySchedule || []).map((r) => ({
        date: r.date,
        startTime: r.startTime || '',
        endTime: r.endTime || '',
        breakStartTime: r.breakStartTime || '',
        breakEndTime: r.breakEndTime || '',
      })),
    )

    const et = items.eventType
    const preset = EVENT_TYPE_OPTIONS.find((o) => o.value && o.value === et)
    if (et && preset) {
      setEventTypeSelect(et)
      setEventTypeOther('')
    } else if (et) {
      setEventTypeSelect(EVENT_TYPE_OTHER)
      setEventTypeOther(et)
    } else {
      setEventTypeSelect('')
      setEventTypeOther('')
    }

    setHasSponsorship(Boolean(items.sponsorshipTitle))
    setSponsorshipTitle(items.sponsorshipTitle || '')

    const coords = items.coordinates
    if (coords && coords.latitude != null && coords.longitude != null) {
      setPlaceCoordinates({ latitude: coords.latitude, longitude: coords.longitude })
    } else {
      setPlaceCoordinates(null)
    }

    setImageUrl(items.image || '')
    setDefaultImage(!!items.image)
    setShowTimezoneOverride(false)
    setTimezoneFromPinError(null)

    queueMicrotask(() => {
      if (addressInputRef.current) {
        addressInputRef.current.value = items.eventLocation || ''
      }
    })

    setFormReady(true)
  }, [items?.id, setDefaultImage])

  useEffect(() => {
    function handleClickOutside(event) {
      if (loading) return
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setEditModal(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [setEditModal, loading])

  const handlerImage = async (e) => {
    const file = e.target.files[0]
    if (!file) return
    try {
      setUploading(true)
      setDefaultImage(false)
      const result = await uploadData({
        key: `event/${dayjs().unix()}-${file.name.replace(/\s+/g, '_')}`,
        data: file,
        options: { accessLevel: 'guest' },
      }).result
      const { url } = await getUrl({
        key: result.key,
        options: { accessLevel: 'guest' },
      })
      const u = new URL(url.toString())
      setImageUrl(`${u.origin}${u.pathname}`)
    } catch (err) {
      openMessageModal('Error', err?.message || 'Image upload failed.')
    } finally {
      setUploading(false)
    }
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    if (!items?.id || !cardId) return

    if (!form.eventName || !form.eventLocation || !form.country) {
      openMessageModal('Heads Up', 'We need a name, location & country to save this one.')
      return
    }
    if (hasSponsorship && !sponsorshipTitle.trim()) {
      openMessageModal('Heads Up', 'Add a sponsorship title, or turn off sponsorship.')
      return
    }
    if (!form.startDate || !form.endDate) {
      openMessageModal('Heads Up', 'Set the start & end dates for this event.')
      return
    }
    if (form.endDate < form.startDate) {
      openMessageModal('Heads Up', 'End date must be on or after the start date.')
      return
    }
    if (!form.dailyStartTime || !form.dailyEndTime || form.dailyEndTime <= form.dailyStartTime) {
      openMessageModal('Heads Up', 'Daily window end time must be after the start time.')
      return
    }
    if (
      (form.breakStartTime || form.breakEndTime) &&
      (!form.breakStartTime ||
        !form.breakEndTime ||
        form.breakEndTime <= form.breakStartTime ||
        form.breakStartTime < form.dailyStartTime ||
        form.breakEndTime > form.dailyEndTime)
    ) {
      openMessageModal('Heads Up', 'Break must sit inside the daily window and end after it starts.')
      return
    }
    for (const row of dailySchedule) {
      if (!row.startTime || !row.endTime || row.endTime <= row.startTime) {
        openMessageModal('Heads Up', `Fix the times for ${row.date} — close must be after open.`)
        return
      }
      if (
        (row.breakStartTime || row.breakEndTime) &&
        (!row.breakStartTime ||
          !row.breakEndTime ||
          row.breakEndTime <= row.breakStartTime ||
          row.breakStartTime < row.startTime ||
          row.breakEndTime > row.endTime)
      ) {
        openMessageModal('Heads Up', `Break for ${row.date} must sit inside that day's window.`)
        return
      }
    }
    if (!placeCoordinates) {
      openMessageModal(
        'Heads Up',
        'Choose an address from the suggestions so we can save the map location.',
      )
      return
    }
    if (!imageUrl) {
      openMessageModal('Heads Up', 'Please upload an event image first.')
      return
    }
    const resolvedType = resolveEventType(eventTypeSelect, eventTypeOther)
    if (eventTypeSelect === EVENT_TYPE_OTHER && !resolvedType) {
      openMessageModal('Heads Up', 'Enter a custom event type, or choose a different option.')
      return
    }

    const profilePayload =
      items.profile && items.profile.name && items.profile.owner && items.profile.type
        ? {
            name: items.profile.name,
            owner: items.profile.owner,
            type: items.profile.type,
          }
        : {
            name: userData?.firstName || 'SaddleFit',
            owner: userData?.email || 'saddlefit',
            type: 'admin',
          }

    try {
      await updateEvent({
        variables: {
          id: cardId,
          input: {
            eventName: form.eventName,
            description: form.description || null,
            eventType: resolvedType,
            sponsorshipTitle: hasSponsorship ? sponsorshipTitle.trim() : null,
            // Local noon keeps the ISO timestamp on the intended calendar day
            // regardless of the admin's timezone (bare YYYY-MM-DD parses as UTC
            // midnight, which shifts a day backward in US timezones).
            date: form.startDate
              ? new Date(`${form.startDate}T12:00:00`).toISOString()
              : new Date(Date.now()).toISOString(),
            city: form.city || null,
            state: form.state || null,
            country: form.country,
            eventLocation: form.eventLocation,
            coordinates: {
              latitude: placeCoordinates.latitude,
              longitude: placeCoordinates.longitude,
            },
            image: imageUrl,
            profile: profilePayload,
            visibility: form.visibility,
            startDate: form.startDate || null,
            endDate: form.endDate || null,
            durationMinutes: parseInt(form.durationMinutes, 10) || 15,
            bufferMinutes: Math.max(0, parseInt(form.bufferMinutes, 10) || 0),
            capacity: Math.max(1, parseInt(form.capacity, 10) || 1),
            timezone: form.timezone,
            dailyStartTime: form.dailyStartTime,
            dailyEndTime: form.dailyEndTime,
            breakStartTime: form.breakStartTime || null,
            breakEndTime: form.breakEndTime || null,
            dailySchedule: dailySchedule.length
              ? dailySchedule.map((r) => ({
                  date: r.date,
                  startTime: r.startTime,
                  endTime: r.endTime,
                  breakStartTime: r.breakStartTime || null,
                  breakEndTime: r.breakEndTime || null,
                }))
              : null,
            fullPaymentAtBooking: Boolean(form.fullPaymentAtBooking),
          },
        },
      })
      setEditModal(false)
      openMessageModal('success', 'Event updated successfully')
    } catch (err) {
      openMessageModal('Error', err?.message || 'Update failed.')
    }
  }

  const field =
    'w-full mt-2 px-4 h-[48px] rounded-[10px] border border-[#c3c9d4] text-[14px] font-montserrat'
  const label =
    'block text-[13px] font-montserrat font-semibold text-[#2b364b] tracking-wide mb-1.5'
  const hint = 'text-[12px] font-montserrat text-[#6b7280] mt-1.5 leading-relaxed'
  const sectionTitle = 'text-lg font-montserrat font-semibold text-[#2b364b] mb-1'
  const sectionCard = 'rounded-2xl border border-[#e8eaef] bg-[#fafbfc] p-6 sm:p-8 space-y-6'

  if (!items?.id || !formReady) {
    return (
      <div>
        <div className="w-full h-full fixed top-0 left-0 z-10 bg-black opacity-60" />
        <div className="z-20 fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white px-10 py-8 shadow-2xl font-montserrat text-[#2b364b]">
          Loading…
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className="w-full h-full fixed top-0 left-0 z-10 bg-black opacity-60" />
      <div
        ref={modalRef}
        className="w-[min(96vw,1120px)] min-w-0 max-w-[1120px] rounded-[24px] bg-white shadow-2xl z-20 fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 max-h-[min(94vh,920px)] overflow-y-auto px-5 py-8 sm:px-12 sm:py-10"
      >
        <div className="absolute top-5 right-5">
          <img
            src={CloseIcon}
            alt="Close"
            className={`w-5 h-5 ${loading ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
            onClick={() => {
              if (!loading) setEditModal(false)
            }}
          />
        </div>
        <h2 className="add-events !text-[36px] !mt-0 mb-8 pr-8">Edit event</h2>

        <form onSubmit={onSubmit} className="space-y-10">
          <div>
            <label className={label} htmlFor="edit-event-name">
              Event name
            </label>
            <input
              id="edit-event-name"
              className={field}
              value={form.eventName}
              onChange={set('eventName')}
              placeholder="Spring Fitting Clinic"
              disabled={loading}
            />
          </div>

          <div>
            <label className={label} htmlFor="edit-event-desc">
              Description
            </label>
            <textarea
              id="edit-event-desc"
              className={field + ' !h-[100px] py-3'}
              value={form.description}
              onChange={set('description')}
              placeholder="Tell riders what to expect."
              disabled={loading}
            />
          </div>

          <div>
            <label className={label} htmlFor="edit-event-type">
              Event type
            </label>
            <select
              id="edit-event-type"
              className={field}
              value={eventTypeSelect}
              onChange={(e) => setEventTypeSelect(e.target.value)}
              disabled={loading}
            >
              {EVENT_TYPE_OPTIONS.map((opt) => (
                <option key={opt.label + opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {eventTypeSelect === EVENT_TYPE_OTHER && (
              <div className="mt-4">
                <label className={label} htmlFor="edit-event-type-other">
                  Describe event type
                </label>
                <input
                  id="edit-event-type-other"
                  className={field}
                  value={eventTypeOther}
                  onChange={(e) => setEventTypeOther(e.target.value)}
                  placeholder="e.g. Trail clinic"
                  disabled={loading}
                />
              </div>
            )}
          </div>

          <div>
            <label className={label} htmlFor="edit-event-visibility">
              Visibility
            </label>
            <select
              id="edit-event-visibility"
              className={field}
              value={form.visibility}
              onChange={set('visibility')}
              disabled={loading}
            >
              <option value="ENABLED">Enabled — visible on the public site</option>
              <option value="DISABLED">Disabled — hidden from the public site</option>
            </select>
            <p className={hint}>
              Disabled events are hidden from the public site — use it as a draft state until you're ready to go live.
            </p>
          </div>

          <div className="space-y-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={hasSponsorship}
                onChange={(e) => {
                  setHasSponsorship(e.target.checked)
                  if (!e.target.checked) setSponsorshipTitle('')
                }}
                className="h-4 w-4 rounded border-[#c3c9d4]"
                disabled={loading}
              />
              <span className={`${label} !mb-0`}>This event has a sponsor</span>
            </label>
            {hasSponsorship && (
              <div>
                <label className={label} htmlFor="edit-event-sponsor-title">
                  Sponsorship title
                </label>
                <input
                  id="edit-event-sponsor-title"
                  className={field}
                  value={sponsorshipTitle}
                  onChange={(e) => setSponsorshipTitle(e.target.value)}
                  placeholder="Shown with the event (e.g. sponsor name)"
                  disabled={loading}
                />
              </div>
            )}
          </div>

          <div className={sectionCard}>
            <h3 className={sectionTitle}>Where</h3>
            <div>
              <label className={label} htmlFor="edit-event-address-search">
                Search address or venue
              </label>
              <input
                id="edit-event-address-search"
                ref={addressInputRef}
                className={field}
                onChange={() => setPlaceCoordinates(null)}
                placeholder="Start typing an address or place name…"
                autoComplete="off"
                disabled={loading}
              />
              <p className={hint}>
                Select a suggestion to set the map pin and fill city, state, and country.
              </p>
              {mapsLoading && (
                <p className="text-[12px] font-montserrat text-[#5c80b6] mt-2">Loading address search…</p>
              )}
              {mapsError && (
                <p className="text-[12px] font-montserrat text-[#f25022] mt-2" role="alert">
                  {mapsError}
                </p>
              )}
            </div>

            <div className="space-y-5">
              <div>
                <label className={label} htmlFor="edit-event-city">
                  City
                </label>
                <input id="edit-event-city" className={field} value={form.city} onChange={set('city')} disabled={loading} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8">
                <div>
                  <label className={label} htmlFor="edit-event-state">
                    State / province
                  </label>
                  <input id="edit-event-state" className={field} value={form.state} onChange={set('state')} disabled={loading} />
                </div>
                <div>
                  <label className={label} htmlFor="edit-event-country">
                    Country
                  </label>
                  <input id="edit-event-country" className={field} value={form.country} onChange={set('country')} disabled={loading} />
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-[#e8eaef] bg-white p-4 space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`${label} !mb-0`}>Timezone</span>
                {timezoneLookupLoading && (
                  <span className="text-[12px] font-montserrat text-[#5c80b6]">Looking up…</span>
                )}
              </div>
              {!showTimezoneOverride ? (
                <>
                  <p className={`${hint} !mt-0`}>
                    <strong className="font-semibold text-[#374151]">{form.timezone}</strong> — set from the map pin when
                    you choose an address above.
                  </p>
                  <button
                    type="button"
                    className="text-[13px] font-montserrat font-semibold text-[#5c80b6] underline"
                    onClick={() => setShowTimezoneOverride(true)}
                    disabled={loading}
                  >
                    Override timezone
                  </button>
                </>
              ) : (
                <>
                  <label className={label} htmlFor="edit-event-tz">
                    IANA timezone
                  </label>
                  <input
                    id="edit-event-tz"
                    className={field + ' max-w-xl'}
                    value={form.timezone}
                    onChange={set('timezone')}
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="text-[13px] font-montserrat text-[#6b7280] underline"
                    onClick={() => {
                      setShowTimezoneOverride(false)
                      if (placeCoordinates) {
                        setTimezoneFromPinError(null)
                        setTimezoneLookupLoading(true)
                        fetchTimezoneIdFromLatLng(placeCoordinates.latitude, placeCoordinates.longitude)
                          .then((tz) => {
                            setForm((f) => ({ ...f, timezone: tz }))
                            setTimezoneFromPinError(null)
                          })
                          .catch((err) => {
                            setTimezoneFromPinError(err?.message || 'Could not load timezone for this location.')
                          })
                          .finally(() => setTimezoneLookupLoading(false))
                      }
                    }}
                    disabled={loading}
                  >
                    Use pin-based timezone
                  </button>
                </>
              )}
              {timezoneFromPinError && (
                <p className="text-[12px] font-montserrat text-[#b45309] mt-1" role="status">
                  {timezoneFromPinError} You can override the timezone below or fix your Google Cloud Time Zone API setup.
                </p>
              )}
            </div>

            <div>
              <label className={label} htmlFor="edit-event-location-label">
                Location shown on site
              </label>
              <input
                id="edit-event-location-label"
                className={field}
                value={form.eventLocation}
                onChange={set('eventLocation')}
                placeholder="Filled when you pick a place above — you can edit"
                disabled={loading}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8">
            <div>
              <label className={label} htmlFor="edit-event-start">
                Start date
              </label>
              <CustomDatePicker
                inputId="edit-event-start"
                value={isoToPickerDate(form.startDate)}
                onChange={(mdy) => setForm((f) => ({ ...f, startDate: pickerToIsoDate(mdy) }))}
                placeholder="MM-DD-YYYY"
                clearable
                floatingLabel={false}
                className="w-full"
                inputClassName={field}
                disabled={loading}
              />
            </div>
            <div>
              <label className={label} htmlFor="edit-event-end">
                End date
              </label>
              <CustomDatePicker
                inputId="edit-event-end"
                value={isoToPickerDate(form.endDate)}
                onChange={(mdy) => setForm((f) => ({ ...f, endDate: pickerToIsoDate(mdy) }))}
                placeholder="MM-DD-YYYY"
                clearable
                floatingLabel={false}
                className="w-full"
                inputClassName={field}
                disabled={loading}
                isValidDate={(current) =>
                  !form.startDate ||
                  current.isSameOrAfter(new Date(form.startDate + 'T00:00:00'), 'day')
                }
              />
            </div>
          </div>

          <div className={sectionCard}>
            <h3 className={sectionTitle}>Booking &amp; sessions</h3>
            <p className={`${hint} !mt-0 max-w-3xl`}>
              Each booking is one start time. <strong className="font-semibold text-[#374151]">Session length</strong> is how
              long the appointment runs for the customer. <strong className="font-semibold text-[#374151]">Buffer</strong> is
              extra time after that before the next slot can start (cleanup, travel, notes).
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 sm:gap-10">
              <div className="min-w-0">
                <label className={label} htmlFor="edit-event-duration">
                  Session length
                </label>
                <input
                  id="edit-event-duration"
                  type="number"
                  min="15"
                  step="15"
                  className={field}
                  value={form.durationMinutes}
                  onChange={set('durationMinutes')}
                  disabled={loading}
                />
                <p className={hint}>Minutes per appointment.</p>
              </div>
              <div className="min-w-0">
                <label className={label} htmlFor="edit-event-buffer">
                  Buffer after session
                </label>
                <input
                  id="edit-event-buffer"
                  type="number"
                  min="0"
                  step="5"
                  className={field}
                  value={form.bufferMinutes}
                  onChange={set('bufferMinutes')}
                  disabled={loading}
                />
                <p className={hint}>Minutes before the next start time. Use 0 for back-to-back starts.</p>
              </div>
              <div className="min-w-0 sm:col-span-2">
                <label className={label} htmlFor="edit-event-capacity">
                  Concurrent bookings per slot
                </label>
                <input
                  id="edit-event-capacity"
                  type="number"
                  min="1"
                  step="1"
                  className={field + ' max-w-xs'}
                  value={form.capacity}
                  onChange={set('capacity')}
                  disabled={loading}
                />
                <p className={hint}>Usually 1 — increase only if your workflow allows more than one booking at the same time.</p>
              </div>
            </div>
            <label className="flex items-start gap-3 mt-6 cursor-pointer max-w-3xl">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 shrink-0 rounded border-[#c3c9d4] text-[#2b364b] focus:ring-[#5c80b6]"
                checked={Boolean(form.fullPaymentAtBooking)}
                onChange={(e) =>
                  setForm((f) => ({ ...f, fullPaymentAtBooking: e.target.checked }))
                }
                disabled={loading}
              />
              <span className={`${hint} !mt-0`}>
                <span className="font-semibold text-[#374151]">On-site / pay in full at booking.</span>{' '}
                Charge the full scan price (from Settings) in one checkout instead of a $50 deposit plus balance later.
              </span>
            </label>
          </div>

          <div className={sectionCard}>
            <h3 className={sectionTitle}>Daily booking window</h3>
            <p className={`${hint} !mt-0 max-w-3xl`}>
              Bookable times use the same window every day between the start &amp; end dates above. Offered times step every{' '}
              <strong className="font-semibold text-[#374151]">session length + buffer</strong> from window open to window
              close. Add a break (e.g. lunch) and no slots will be offered during that time.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8">
              <div className="min-w-0">
                <label className={label} htmlFor="edit-event-daily-start">
                  Window opens
                </label>
                <input
                  id="edit-event-daily-start"
                  type="time"
                  className={field}
                  value={form.dailyStartTime}
                  onChange={set('dailyStartTime')}
                  disabled={loading}
                />
              </div>
              <div className="min-w-0">
                <label className={label} htmlFor="edit-event-daily-end">
                  Window closes
                </label>
                <input
                  id="edit-event-daily-end"
                  type="time"
                  className={field}
                  value={form.dailyEndTime}
                  onChange={set('dailyEndTime')}
                  disabled={loading}
                />
              </div>
              <div className="min-w-0">
                <label className={label} htmlFor="edit-event-break-start">
                  Break starts (optional)
                </label>
                <input
                  id="edit-event-break-start"
                  type="time"
                  className={field}
                  value={form.breakStartTime}
                  onChange={set('breakStartTime')}
                  disabled={loading}
                />
              </div>
              <div className="min-w-0">
                <label className={label} htmlFor="edit-event-break-end">
                  Break ends (optional)
                </label>
                <input
                  id="edit-event-break-end"
                  type="time"
                  className={field}
                  value={form.breakEndTime}
                  onChange={set('breakEndTime')}
                  disabled={loading}
                />
              </div>
            </div>
          </div>

          <div className={sectionCard}>
            <h3 className={sectionTitle}>Per-day overrides (optional)</h3>
            <p className={`${hint} !mt-0 max-w-3xl`}>
              Need a different schedule on specific days? Add overrides for any day in the range and those will replace the
              daily defaults above. Days you don&apos;t list keep using the defaults.
            </p>

            {form.startDate && form.endDate && form.endDate >= form.startDate ? (
              (() => {
                const allDates = enumerateDates(form.startDate, form.endDate)
                const usedDates = new Set(dailySchedule.map((r) => r.date))
                const availableDates = allDates.filter((d) => !usedDates.has(d))
                const compactField =
                  'w-full px-2 h-[36px] rounded-[8px] border border-[#c3c9d4] text-[13px] font-montserrat'
                const updateRow = (date, k, v) =>
                  setDailySchedule((rows) => rows.map((r) => (r.date === date ? { ...r, [k]: v } : r)))
                return (
                  <>
                    {dailySchedule.length > 0 && (
                      <div className="hidden lg:grid grid-cols-[minmax(120px,150px)_repeat(4,minmax(80px,1fr))_28px] gap-2 items-end px-3 text-[11px] font-montserrat font-semibold text-[#6b7280] uppercase tracking-wide">
                        <div>Day</div>
                        <div>Opens</div>
                        <div>Closes</div>
                        <div>Break starts</div>
                        <div>Break ends</div>
                        <div />
                      </div>
                    )}
                    {dailySchedule.length > 0 && (
                      <div className="space-y-2">
                        {dailySchedule
                          .slice()
                          .sort((a, b) => a.date.localeCompare(b.date))
                          .map((row) => (
                            <div
                              key={row.date}
                              className="grid grid-cols-2 lg:grid-cols-[minmax(120px,150px)_repeat(4,minmax(80px,1fr))_28px] gap-2 items-center px-3 py-2 rounded-lg border border-[#e8eaef] bg-white"
                            >
                              <div className="col-span-2 lg:col-span-1 text-[13px] font-semibold text-[#2b364b]">
                                {formatDateLabel(row.date)}
                              </div>
                              <input
                                type="time"
                                aria-label={`Opens on ${row.date}`}
                                className={compactField}
                                value={row.startTime}
                                onChange={(e) => updateRow(row.date, 'startTime', e.target.value)}
                                disabled={loading}
                              />
                              <input
                                type="time"
                                aria-label={`Closes on ${row.date}`}
                                className={compactField}
                                value={row.endTime}
                                onChange={(e) => updateRow(row.date, 'endTime', e.target.value)}
                                disabled={loading}
                              />
                              <input
                                type="time"
                                aria-label={`Break starts on ${row.date}`}
                                className={compactField}
                                value={row.breakStartTime}
                                onChange={(e) => updateRow(row.date, 'breakStartTime', e.target.value)}
                                disabled={loading}
                              />
                              <input
                                type="time"
                                aria-label={`Break ends on ${row.date}`}
                                className={compactField}
                                value={row.breakEndTime}
                                onChange={(e) => updateRow(row.date, 'breakEndTime', e.target.value)}
                                disabled={loading}
                              />
                              <button
                                type="button"
                                onClick={() => setDailySchedule((rows) => rows.filter((r) => r.date !== row.date))}
                                aria-label={`Remove override for ${row.date}`}
                                className="justify-self-end h-7 w-7 rounded-full text-[#f25022] hover:bg-red-50 text-lg leading-none"
                                disabled={loading}
                              >
                                ×
                              </button>
                            </div>
                          ))}
                      </div>
                    )}

                    <div className="flex items-end gap-3 max-w-md">
                      <select
                        id="edit-event-add-override"
                        aria-label="Add override for"
                        className={compactField}
                        value=""
                        onChange={(e) => {
                          const date = e.target.value
                          if (!date) return
                          setDailySchedule((rows) => [
                            ...rows,
                            {
                              date,
                              startTime: form.dailyStartTime,
                              endTime: form.dailyEndTime,
                              breakStartTime: form.breakStartTime || '',
                              breakEndTime: form.breakEndTime || '',
                            },
                          ])
                        }}
                        disabled={loading}
                      >
                        <option value="">
                          {availableDates.length ? '+ Add override for…' : 'No more days in range'}
                        </option>
                        {availableDates.map((d) => (
                          <option key={d} value={d}>
                            {formatDateLabel(d)} ({d})
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                )
              })()
            ) : (
              <p className={`${hint} !mt-0`}>Pick a start &amp; end date above to add per-day overrides.</p>
            )}
          </div>

          <div>
            <label className={label}>Event image</label>
            <input
              type="file"
              accept="image/*"
              ref={imageRef}
              onChange={handlerImage}
              className="sr-only"
              id="edit-event-image-input"
              disabled={loading}
            />
            <div className="flex items-center gap-4 mt-2">
              {imageUrl || items?.image ? (
                <img
                  src={imageUrl || items?.image}
                  alt="Event preview"
                  className="w-[88px] h-[88px] object-cover rounded-[10px] border border-[#e8eaef]"
                />
              ) : (
                <div className="w-[88px] h-[88px] rounded-[10px] border border-dashed border-[#c3c9d4] bg-[#fafbfc] flex items-center justify-center text-[11px] font-montserrat text-[#9ca3af]">
                  {uploading ? '…' : 'No image'}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <button
                  type="button"
                  onClick={() => imageRef.current?.click()}
                  disabled={uploading || loading}
                  className="inline-flex items-center justify-center h-[44px] px-5 rounded-full bg-[#2b364b] text-white text-[13px] font-montserrat font-semibold tracking-wide hover:opacity-90 disabled:opacity-60 transition"
                >
                  {uploading ? 'Uploading…' : imageUrl ? 'Replace image' : 'Choose image'}
                </button>
                <p className={`${hint} !mt-2`}>PNG, JPG or JPEG · up to 5 MB</p>
              </div>
            </div>
          </div>

          <div className="flex flex-col-reverse sm:flex-row gap-4 sm:gap-5 pt-4 justify-center items-stretch sm:items-center">
            <Button
              type="button"
              onClick={() => setEditModal(false)}
              disabled={loading}
              className="!border-[#2b364b] w-full sm:w-[min(100%,200px)] cursor-pointer h-[56px] rounded-[56px]"
              primaryOutline
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={loading}
              disabled={loading}
              className="text-white cursor-pointer w-full sm:w-[min(100%,200px)] h-[56px] rounded-[56px]"
              primary
            >
              Save changes
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default EditEventModal
